import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  LogIn, LogOut, Clock, Download, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, AlertTriangle, Coffee, CalendarDays,
  List, BarChart3, Briefcase,
} from "lucide-react";
import { useToast } from "../../../components/admin/ToastContext";
import {
  fetchAllEmployees, fetchTodaysAttendance,
  buildAttendanceData, getMonthDays, getWeekDays, toDateStr, punchAttendance,
} from "../../../services/attendanceService";
import { PageSkeleton } from "../../../components/admin/LoadingSkeleton";

const DAY_KEYS = [
  "ess.attendance.daySun", "ess.attendance.dayMon", "ess.attendance.dayTue",
  "ess.attendance.dayWed", "ess.attendance.dayThu", "ess.attendance.dayFri",
  "ess.attendance.daySat",
];

const STATUS_STYLE = {
  present: "bg-emerald-50 text-emerald-700 border-emerald-200 ring-1 ring-emerald-200/50",
  absent: "bg-red-50 text-red-700 border-red-200 ring-1 ring-red-200/50",
  on_leave: "bg-blue-50 text-blue-700 border-blue-200 ring-1 ring-blue-200/50",
  future: "bg-white text-gray-300 border-gray-100",
  weekend: "bg-gray-50 text-gray-400 border-gray-100",
};

const STATUS_DOT = {
  present: "bg-emerald-500",
  absent: "bg-red-500",
  on_leave: "bg-blue-500",
  future: "bg-gray-200",
  weekend: "bg-gray-200",
};

function fmtTime(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function fmtDate(d) {
  if (!d) return "—";
  if (typeof d === "string") d = new Date(d);
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
}

function getMonthName(m, intl) {
  const names = [
    "ess.dashboard.monthJan", "ess.dashboard.monthFeb", "ess.dashboard.monthMar",
    "ess.dashboard.monthApr", "ess.dashboard.monthMay", "ess.dashboard.monthJun",
    "ess.dashboard.monthJul", "ess.dashboard.monthAug", "ess.dashboard.monthSep",
    "ess.dashboard.monthOct", "ess.dashboard.monthNov", "ess.dashboard.monthDec",
  ];
  return intl.formatMessage({ id: names[m] });
}

const ESSAttendancePage = () => {
  const intl = useIntl();
  const addToast = useToast();
  const { user } = useSelector((state) => state.auth);

  const [loaded, setLoaded] = useState(false);
  const [employeeId, setEmployeeId] = useState(null);
  const [checkedIn, setCheckedIn] = useState(false);
  const [workingDays, setWorkingDays] = useState([1, 2, 3, 4, 5]);
  const [records, setRecords] = useState([]);
  const [allRecords, setAllRecords] = useState([]);

  const [periodType, setPeriodType] = useState("month");
  const now = new Date();
  const [periodYear, setPeriodYear] = useState(now.getFullYear());
  const [periodMonth, setPeriodMonth] = useState(now.getMonth());
  const [periodWeekStart, setPeriodWeekStart] = useState(getWeekDays(now)[0]);
  const [periodDay, setPeriodDay] = useState(now);

  const [viewMode, setViewMode] = useState("calendar");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const dayName = useCallback((dt) => {
    const d = typeof dt === "string" ? new Date(dt) : dt;
    return intl.formatMessage({ id: DAY_KEYS[d.getDay()] });
  }, [intl]);

  const fmtHours = useCallback((hours) => {
    if (hours === null || hours === undefined) return "—";
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return intl.formatMessage({ id: "ess.attendance.hoursFormat" }, { h, m });
  }, [intl]);

  const getDateRange = useCallback(() => {
    if (periodType === "month") {
      const from = `${periodYear}-${String(periodMonth + 1).padStart(2, "0")}-01`;
      const last = new Date(periodYear, periodMonth + 1, 0).getDate();
      const to = `${periodYear}-${String(periodMonth + 1).padStart(2, "0")}-${last}`;
      return { from, to };
    }
    if (periodType === "week") {
      const from = toDateStr(periodWeekStart);
      const end = new Date(periodWeekStart);
      end.setDate(end.getDate() + 6);
      return { from, to: toDateStr(end) };
    }
    return { from: toDateStr(periodDay), to: toDateStr(periodDay) };
  }, [periodType, periodYear, periodMonth, periodWeekStart, periodDay]);

  const loadData = useCallback(async () => {
    try {
      const allEmployees = await fetchAllEmployees();
      const currentEmp = allEmployees.find((e) => e.work_email === user?.email) || null;
      const empId = currentEmp?.id || currentEmp?.employee_id;
      setEmployeeId(empId);

      if (!empId) { setLoaded(true); return; }

      const todaysAtts = await fetchTodaysAttendance(empId);
      const todayStr = new Date().toISOString().split("T")[0];
      const openRecord = todaysAtts.find((r) => {
        const ci = r.check_in ? new Date(r.check_in).toISOString().split("T")[0] : null;
        return ci === todayStr && r.check_in && !r.check_out;
      });
      setCheckedIn(!!openRecord);

      const wDays = [1, 2, 3, 4, 5];
      setWorkingDays(wDays);

      const fullFrom = `${periodYear}-${String(periodMonth + 1).padStart(2, "0")}-01`;
      const last = new Date(periodYear, periodMonth + 1, 0).getDate();
      const fullTo = `${periodYear}-${String(periodMonth + 1).padStart(2, "0")}-${last}`;
      const all = await buildAttendanceData(empId, fullFrom, fullTo, wDays);
      setAllRecords(all);

      const { from, to } = getDateRange();
      const filtered = await buildAttendanceData(empId, from, to, wDays);
      setRecords(filtered);
    } catch (_) {}
    setLoaded(true);
  }, [user, periodYear, periodMonth, getDateRange]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!employeeId) return;
    const { from, to } = getDateRange();
    buildAttendanceData(employeeId, from, to, workingDays).then(setRecords);
  }, [periodType, periodYear, periodMonth, periodWeekStart, periodDay, employeeId, workingDays, getDateRange]);

  const navigatePeriod = (dir) => {
    if (periodType === "month") {
      let m = periodMonth + dir;
      let y = periodYear;
      if (m < 0) { m = 11; y--; }
      if (m > 11) { m = 0; y++; }
      setPeriodMonth(m);
      setPeriodYear(y);
    } else if (periodType === "week") {
      const d = new Date(periodWeekStart);
      d.setDate(d.getDate() + 7 * dir);
      setPeriodWeekStart(d);
    } else {
      const d = new Date(periodDay);
      d.setDate(d.getDate() + dir);
      setPeriodDay(d);
    }
  };

  const displayStatus = useCallback((r) => {
    if (r.status === "present" && r.isLate) return "late";
    return r.status;
  }, []);

  const filteredRecords = useMemo(() => {
    if (statusFilter === "all") return records;
    if (statusFilter === "late") return records.filter((r) => r.status === "present" && r.isLate);
    return records.filter((r) => r.status === statusFilter);
  }, [records, statusFilter]);

  const tableRecords = useMemo(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return filteredRecords
      .filter((r) => new Date(r.date) <= today)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [filteredRecords]);

  const totalPages = Math.max(1, Math.ceil(tableRecords.length / PAGE_SIZE));
  const paginated = tableRecords.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [statusFilter, periodType, periodYear, periodMonth, periodWeekStart, periodDay]);

  const handlePunch = async () => {
    if (!employeeId) return;
    try {
      await punchAttendance(employeeId, checkedIn);
      addToast(checkedIn ? intl.formatMessage({ id: "ess.attendance.checkoutDone" }) : intl.formatMessage({ id: "ess.attendance.checkinDone" }), "success");
      window.location.reload();
    } catch (_) {
      addToast(intl.formatMessage({ id: "ess.attendance.punchError" }), "error");
    }
  };

  const handleDownloadCSV = () => {
    const headers = [
      intl.formatMessage({ id: "ess.attendance.csvDate" }),
      intl.formatMessage({ id: "ess.attendance.csvDay" }),
      intl.formatMessage({ id: "ess.attendance.csvIn" }),
      intl.formatMessage({ id: "ess.attendance.csvOut" }),
      intl.formatMessage({ id: "ess.attendance.csvHours" }),
      intl.formatMessage({ id: "ess.attendance.csvStatus" }),
    ];
    const rows = tableRecords.map((r) => [
      fmtDate(r.date), dayName(r.date), fmtTime(r.checkIn), fmtTime(r.checkOut),
      fmtHours(r.hours), r.status,
    ]);
    const csv = [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    addToast(intl.formatMessage({ id: "ess.attendance.downloadSuccess" }), "success");
  };

  if (!loaded) return <PageSkeleton />;

  const monthPresent = allRecords.filter((r) => r.status === "present").length;
  const monthTotal = allRecords.filter((r) => r.status !== "weekend" && r.status !== "on_leave" && r.status !== "future").length;
  const presencePct = monthTotal ? Math.round((monthPresent / monthTotal) * 100) : 100;

  const lateCount = records.filter((r) => r.status === "present" && r.isLate).length;
  const absentCount = records.filter((r) => r.status === "absent").length;
  const presentCount = records.filter((r) => r.status === "present" && !r.isLate).length;

  const periodTitle = () => {
    if (periodType === "month") return `${getMonthName(periodMonth, intl)} ${periodYear}`;
    if (periodType === "week") {
      const end = new Date(periodWeekStart);
      end.setDate(end.getDate() + 6);
      return `${fmtDate(periodWeekStart)} - ${fmtDate(end)}`;
    }
    return `${dayName(periodDay)} ${fmtDate(periodDay)}`;
  };

  const renderCalendar = () => {
    const days = getMonthDays(periodYear, periodMonth);
    const shortDays = [1, 2, 3, 4, 5, 6, 7].map((d) =>
      intl.formatMessage({ id: `ess.attendance.day${["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d === 7 ? 0 : d]}` })
    );
    const recMap = {};
    for (const r of allRecords) recMap[r.dateStr] = r;

    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="grid grid-cols-7 gap-0.5">
          {shortDays.map((n, i) => (
            <div key={i} className="text-center text-[10px] font-bold uppercase tracking-wider text-gray-400 py-2">
              {n}
            </div>
          ))}
          {days.map((d, i) => {
            if (!d) return <div key={`e${i}`} />;
            const key = toDateStr(d);
            const rec = recMap[key];
            const isToday = toDateStr(new Date()) === key;
            const dsp = rec ? displayStatus(rec) : null;
            const dotColor = dsp === "late" ? "bg-amber-500" : (STATUS_DOT[rec?.status] || "bg-gray-300");
            return (
              <div
                key={key}
                className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-medium transition-colors cursor-pointer hover:ring-2 hover:ring-emerald-400 ${
                  dsp === "late" ? "bg-amber-50 text-amber-700" :
                  rec?.status === "present" ? "bg-emerald-50 text-emerald-700" :
                  rec?.status === "absent" ? "bg-red-50 text-red-700" :
                  rec?.status === "on_leave" ? "bg-blue-50 text-blue-700" :
                  rec?.status === "future" ? "bg-white text-gray-300" :
                  "text-gray-300"
                } ${isToday ? "ring-2 ring-emerald-500" : ""}`}
                title={rec ? `${fmtDate(rec.date)} ${dsp === "late" ? "⚠ Retard" : ""}${rec.ghostCount > 0 ? ` · ${rec.ghostCount} fantôme(s)` : ""}` : ""}
              >
                <span className={`text-xs font-bold ${isToday ? "text-emerald-600" : ""}`}>{d.getDate()}</span>
                {rec && rec.status !== "weekend" && (
                  <div className={`w-1.5 h-1.5 rounded-full mt-0.5 ${dotColor}`} />
                )}
                {rec?.checkIn && (
                  <span className="text-[8px] opacity-70 mt-0.5">{fmtTime(rec.checkIn)}</span>
                )}
                {rec?.crossDay && (
                  <span className="text-[8px] text-blue-500 font-bold">↷</span>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-500">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> Présent</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> Retard</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Absent</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" /> Congé</span>
          <span className="flex items-center gap-1"><span className="w-0.5 h-0.5 rounded-full bg-gray-200" /> À venir</span>
          <span className="flex items-center gap-1 text-[10px]">↷ Chevauchement</span>
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(periodWeekStart);
    const recMap = {};
    for (const r of records) recMap[r.dateStr] = r;

    return (
      <div className="grid grid-cols-7 gap-2">
          {weekDays.map((d, i) => {
          const key = toDateStr(d);
          const rec = recMap[key];
          const isToday = toDateStr(new Date()) === key;
          const dsp = rec ? displayStatus(rec) : null;
          return (
            <div
              key={key}
              className={`bg-white rounded-xl border shadow-sm p-3 flex flex-col items-center text-center transition-all ${
                isToday ? "border-emerald-400 ring-2 ring-emerald-200" : "border-gray-200"
              } ${rec?.status === "absent" ? "bg-red-50/30" : dsp === "late" ? "bg-amber-50/30" : ""}`}
            >
              <span className="text-[10px] font-bold uppercase text-gray-400">{dayName(d)}</span>
              <span className="text-lg font-bold text-gray-800 mt-1">{d.getDate()}</span>
              {rec?.status === "weekend" ? (
                <span className="text-xs text-gray-300 mt-2">—</span>
              ) : rec?.checkIn ? (
                <div className="mt-2 space-y-1">
                  <div className="flex items-center gap-1 text-xs">
                    <LogIn size={10} className="text-emerald-500" />
                    <span className="font-mono font-medium text-gray-700">{fmtTime(rec.checkIn)}</span>
                    {rec.isLate && <AlertTriangle size={10} className="text-amber-500" />}
                  </div>
                  <div className="flex items-center gap-1 text-xs">
                    <LogOut size={10} className="text-red-400" />
                    <span className="font-mono text-gray-600">{fmtTime(rec.checkOut)}</span>
                    {rec.crossDay && <span className="text-blue-500 text-[10px] font-bold">↷</span>}
                  </div>
                  <div className="text-[10px] font-mono text-gray-500">{fmtHours(rec.hours)}</div>
                  <div className={`mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                    dsp === "late" ? "bg-amber-100 text-amber-700" :
                    rec.status === "present" ? "bg-emerald-100 text-emerald-700" : ""
                  }`}>
                    {dsp === "late" && <AlertTriangle size={10} />}
                    {intl.formatMessage({ id: `ess.status.${dsp}` })}
                  </div>
                  {rec.ghostCount > 0 && <span className="text-[8px] text-gray-400">{rec.ghostCount} fantôme(s)</span>}
                </div>
              ) : rec?.status === "absent" ? (
                <div className="mt-2 flex flex-col items-center gap-1">
                  <XCircle size={16} className="text-red-400" />
                  <span className="text-[10px] font-semibold text-red-500">{intl.formatMessage({ id: "ess.status.absent" })}</span>
                </div>
              ) : rec?.status === "on_leave" ? (
                <div className="mt-2 flex flex-col items-center gap-1">
                  <Briefcase size={16} className="text-blue-400" />
                  <span className="text-[10px] font-semibold text-blue-500">{intl.formatMessage({ id: "ess.status.on_leave" })}</span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayView = () => {
    const key = toDateStr(periodDay);
    const rec = records.find((r) => r.dateStr === key);
    const isToday = toDateStr(new Date()) === key;

    return (
      <div className={`bg-white rounded-xl border shadow-sm p-6 ${isToday ? "border-emerald-400 ring-2 ring-emerald-200" : "border-gray-200"}`}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-gray-400">{dayName(periodDay)}</span>
            <h3 className="text-lg font-bold text-gray-900">{fmtDate(periodDay)}</h3>
          </div>
          {rec && (
            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${STATUS_STYLE[rec.status] || ""}`}>
              {intl.formatMessage({ id: `ess.status.${rec.status}` })}
            </span>
          )}
        </div>
        {!rec || rec.status === "weekend" ? (
          <div className="text-center py-8 text-gray-400">
            <Coffee size={32} className="mx-auto mb-2" />
            <p className="text-sm">{intl.formatMessage({ id: rec?.status === "weekend" ? "ess.attendance.weekendDay" : "ess.common.noData" })}</p>
          </div>
        ) : rec.status === "absent" ? (
          <div className="text-center py-8">
            <XCircle size={40} className="mx-auto mb-3 text-red-400" />
            <p className="text-sm font-semibold text-red-600">{intl.formatMessage({ id: "ess.attendance.absentDay" })}</p>
            <p className="text-xs text-gray-500 mt-1">{intl.formatMessage({ id: "ess.attendance.absentDayDesc" })}</p>
          </div>
        ) : rec.status === "on_leave" ? (
          <div className="text-center py-8">
            <Briefcase size={40} className="mx-auto mb-3 text-blue-400" />
            <p className="text-sm font-semibold text-blue-600">{rec.leaveName || intl.formatMessage({ id: "ess.attendance.onLeaveDay" })}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <LogIn size={20} className="mx-auto mb-1 text-emerald-500" />
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: "ess.attendance.colIn" })}</p>
                <p className="text-xl font-bold text-gray-900 mt-1 font-mono">{fmtTime(rec.checkIn)}</p>
                {rec.isLate && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 mt-1">
                    <AlertTriangle size={10} /> {intl.formatMessage({ id: "ess.attendance.late" })}
                  </span>
                )}
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <LogOut size={20} className="mx-auto mb-1 text-red-400" />
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: "ess.attendance.colOut" })}</p>
                <p className="text-xl font-bold text-gray-900 mt-1 font-mono">{fmtTime(rec.checkOut)}</p>
                {rec.crossDay ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-600 mt-1">↷ {intl.formatMessage({ id: "ess.attendance.crossDay" })}</span>
                ) : rec.isEarly ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 mt-1">
                    <AlertTriangle size={10} /> {intl.formatMessage({ id: "ess.attendance.early" })}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="bg-violet-50 rounded-xl p-4 text-center">
              <Clock size={20} className="mx-auto mb-1 text-violet-500" />
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: "ess.attendance.colDuration" })}</p>
              <p className="text-2xl font-bold text-gray-900 mt-1 font-mono">{fmtHours(rec.hours)}</p>
              {rec.ghostCount > 0 && <p className="text-[9px] text-gray-400 mt-1">{rec.ghostCount} lecture(s) fantôme(s)</p>}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{intl.formatMessage({ id: "ess.attendance.title" })}</h1>
          <p className="text-gray-500 text-sm mt-1">{intl.formatMessage({ id: "ess.attendance.subtitle" })}</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 shadow-sm p-1">
          {["month", "week", "day"].map((t) => (
            <button
              key={t}
              onClick={() => { setPeriodType(t); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                periodType === t ? "bg-emerald-500 text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              }`}
            >
              {t === "month" ? <CalendarDays size={14} /> : t === "week" ? <List size={14} /> : <Clock size={14} />}
              {intl.formatMessage({ id: `ess.attendance.period${t.charAt(0).toUpperCase() + t.slice(1)}` })}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 shadow-sm p-3">
        <button onClick={() => navigatePeriod(-1)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <ChevronLeft size={18} />
        </button>
        <h2 className="text-sm font-bold text-gray-800">{periodTitle()}</h2>
        <button onClick={() => navigatePeriod(1)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 border-t-4 border-t-emerald-500 shadow-sm p-5 text-center transition-all hover:shadow-md">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: "ess.attendance.today" })}</p>
          <p className={`text-xl font-bold mt-3 ${checkedIn ? "text-emerald-600" : "text-gray-400"}`}>
            {checkedIn ? intl.formatMessage({ id: "ess.attendance.statusCheckIn" }) : intl.formatMessage({ id: "ess.attendance.statusCheckOut" })}
          </p>
          <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1">
            <Clock size={12} /> {new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
          </p>
          <button onClick={handlePunch} className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-white text-xs font-bold rounded-lg transition-all shadow-sm ${
            checkedIn ? "bg-red-500 hover:bg-red-600" : "bg-emerald-500 hover:bg-emerald-600"
          }`}>
            {checkedIn ? <><LogOut size={14} /> {intl.formatMessage({ id: "ess.attendance.punchOut" })}</> : <><LogIn size={14} /> {intl.formatMessage({ id: "ess.attendance.punchIn" })}</>}
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 border-t-4 border-t-blue-500 shadow-sm p-5 text-center transition-all hover:shadow-md">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: "ess.attendance.thisMonth" })}</p>
          <p className="text-2xl font-extrabold text-blue-600 mt-2">{monthPresent} <span className="text-gray-400 text-lg font-normal">/ {monthTotal}</span></p>
          <div className="mt-3 w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full bg-blue-500 transition-all duration-500" style={{ width: `${presencePct}%` }} />
          </div>
          <p className="text-[10px] text-emerald-600 font-bold mt-1.5 flex items-center justify-center gap-1">
            <CheckCircle2 size={10} /> {intl.formatMessage({ id: "ess.attendance.presencePct" }, { pct: presencePct })}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 border-t-4 border-t-violet-500 shadow-sm p-5 text-center transition-all hover:shadow-md">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{intl.formatMessage({ id: "ess.attendance.dailyAvg" })}</p>
          <p className="text-2xl font-extrabold text-violet-600 mt-2">
            {(() => {
              const withH = allRecords.filter((r) => r.hours !== null && r.hours !== undefined);
              if (!withH.length) return "—";
              const avg = withH.reduce((s, r) => s + r.hours, 0) / withH.length;
              return fmtHours(avg);
            })()}
          </p>
          <p className="text-[10px] text-gray-400 mt-1.5 flex items-center justify-center gap-1">
            <BarChart3 size={12} /> {intl.formatMessage({ id: "ess.attendance.avgHoursPerDay" })}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 border-t-4 border-t-amber-500 shadow-sm p-5 transition-all hover:shadow-md">
          <p className="text-xs font-bold uppercase tracking-wider text-gray-500 text-center">{intl.formatMessage({ id: "ess.attendance.periodSummary" })}</p>
          <div className="flex justify-center gap-4 mt-3">
            <div className="text-center">
              <p className="text-lg font-bold text-emerald-600">{presentCount}</p>
              <p className="text-[10px] text-gray-400">{intl.formatMessage({ id: "ess.status.present" })}</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-amber-600">{lateCount}</p>
              <p className="text-[10px] text-gray-400">{intl.formatMessage({ id: "ess.attendance.late" })}</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-red-600">{absentCount}</p>
              <p className="text-[10px] text-gray-400">{intl.formatMessage({ id: "ess.status.absent" })}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-wrap gap-3 items-center justify-between">
        <div className="flex items-center gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 bg-white">
            <option value="all">{intl.formatMessage({ id: "ess.attendance.filterAll" })}</option>
            <option value="present">{intl.formatMessage({ id: "ess.status.present" })}</option>
            <option value="late">{intl.formatMessage({ id: "ess.attendance.late" })}</option>
            <option value="absent">{intl.formatMessage({ id: "ess.status.absent" })}</option>
            <option value="on_leave">{intl.formatMessage({ id: "ess.status.on_leave" })}</option>
          </select>
        </div>
        <button onClick={handleDownloadCSV}
          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white text-sm font-semibold rounded-lg hover:bg-emerald-600 active:bg-emerald-700 transition-all shadow-sm">
          <Download size={14} /> {intl.formatMessage({ id: "ess.attendance.download" })}
        </button>
      </div>

      <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 shadow-sm p-1 w-fit">
        <button
          onClick={() => setViewMode("calendar")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
            viewMode === "calendar" ? "bg-emerald-500 text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          <CalendarDays size={14} /> {intl.formatMessage({ id: "ess.attendance.calendarView" })}
        </button>
        <button
          onClick={() => setViewMode("table")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
            viewMode === "table" ? "bg-emerald-500 text-white shadow-sm" : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          <List size={14} /> {intl.formatMessage({ id: "ess.attendance.listView" })}
        </button>
      </div>

      {viewMode === "calendar" && (
        <>
          {periodType === "month" && renderCalendar()}
          {periodType === "week" && renderWeekView()}
          {periodType === "day" && renderDayView()}
        </>
      )}

      {viewMode === "table" && (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-gray-500 border-b border-gray-100">
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.attendance.colDate" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden sm:table-cell">{intl.formatMessage({ id: "ess.attendance.colDay" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.attendance.colIn" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.attendance.colOut" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400 hidden md:table-cell">{intl.formatMessage({ id: "ess.attendance.colDuration" })}</th>
                <th className="px-5 py-3.5 font-bold uppercase text-[10px] tracking-widest text-slate-400">{intl.formatMessage({ id: "ess.attendance.colStatus" })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginated.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-gray-400">{intl.formatMessage({ id: "ess.common.noRecords" })}</td>
                </tr>
              )}
              {paginated.map((row, i) => {
                const dsp = displayStatus(row);
                return (
                <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5 font-semibold text-gray-900">
                    {fmtDate(row.date)}
                    {row.ghostCount > 0 && <span className="ml-1.5 text-[10px] text-gray-400" title={`${row.ghostCount} fantôme(s)`}>·{row.ghostCount}</span>}
                  </td>
                  <td className="px-5 py-3.5 text-gray-500 hidden sm:table-cell">{dayName(row.date)}</td>
                  <td className="px-5 py-3.5">
                    <span className="font-mono text-sm font-medium text-gray-700">{fmtTime(row.checkIn)}</span>
                    {row.isLate && <span className="ml-1.5 text-[10px] font-bold text-amber-600">⚠</span>}
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="font-mono text-sm font-medium text-gray-700">{fmtTime(row.checkOut)}</span>
                    {row.crossDay && <span className="ml-1.5 text-[10px] font-bold text-blue-500" title="Session chevauche J+1">↷</span>}
                    {!row.crossDay && row.isEarly && <span className="ml-1.5 text-[10px] font-bold text-amber-600">⚠</span>}
                  </td>
                  <td className="px-5 py-3.5 text-gray-600 hidden md:table-cell font-mono text-xs">{fmtHours(row.hours)}</td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLE[row.status] || (dsp === "late" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-green-50 text-green-700 border-green-100")}`}>
                      {dsp === "late" && <AlertTriangle size={10} />}
                      {intl.formatMessage({ id: `ess.status.${dsp}` }) || dsp}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50/30">
            <span className="text-xs text-gray-500">{intl.formatMessage({ id: "ess.common.pagination" }, { count: tableRecords.length, page, total: totalPages })}</span>
            <div className="flex items-center gap-1.5">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
                className="p-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-30 rounded border border-gray-200 hover:bg-white transition-colors bg-white">
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => setPage(p)}
                  className={`px-3 py-1 text-xs font-bold rounded border transition-colors ${p === page ? "bg-emerald-500 text-white border-emerald-500 shadow-sm" : "border-gray-200 text-gray-600 hover:bg-white bg-white"}`}>
                  {p}
                </button>
              ))}
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
                className="p-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-30 rounded border border-gray-200 hover:bg-white transition-colors bg-white">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ESSAttendancePage;
